import { Fragment, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getResourcePath } from '@spotahome/soyuz/client';

import {
  getPoiRequest,
  setPoiRequest,
  deletePoiRequest
} from '@spotahome/marketplace-common/src/api/poi-client';
import PlacesAutocomplete from '@spotahome/marketplace-common/src/components/PlacesAutocomplete';
import {
  POI_CIRCLE_COLOR,
  createPoiMarkerOverlay
} from '@spotahome/marketplace-common/src/components/MapWoosmap/poi-overlay';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import './InteractiveMap.scss';

const BIG_MARKER = getResourcePath(
  '/bundles/sahapp/images/ui/big-marker-selected.svg'
);
// SVG is 146×146 with the circle centered; render at intrinsic size and
// anchor at the center so the lat/lng sits under the middle of the circle.
const PROPERTY_MARKER_SIZE = 146;
const POI_CIRCLE_RADIUS_METERS = 100;

// `woosmap.map.Marker` keeps a default rounded-rectangle container behind the
// icon even with a plain `icon.url`, which shows up as a white box around the
// transparent circle SVG. Render through OverlayView for a bare <img> with no
// Woosmap chrome — same workaround as poi-overlay.
const createPropertyMarkerOverlay = position => {
  class PropertyMarkerOverlay extends window.woosmap.map.OverlayView {
    constructor() {
      super();
      this.position = position;
      this.containerDiv = null;
    }

    onAdd() {
      this.containerDiv = document.createElement('div');
      this.containerDiv.style.position = 'absolute';
      const img = document.createElement('img');
      img.src = BIG_MARKER;
      img.width = PROPERTY_MARKER_SIZE;
      img.height = PROPERTY_MARKER_SIZE;
      img.style.display = 'block';
      this.containerDiv.appendChild(img);
      this.getPanes().overlayMouseTarget.appendChild(this.containerDiv);
    }

    draw() {
      if (!this.containerDiv) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (!point) return;
      const half = PROPERTY_MARKER_SIZE / 2;
      this.containerDiv.style.left = `${point.x - half}px`;
      this.containerDiv.style.top = `${point.y - half}px`;
    }

    onRemove() {
      if (this.containerDiv?.parentNode) {
        this.containerDiv.parentNode.removeChild(this.containerDiv);
      }
      this.containerDiv = null;
    }
  }

  return new PropertyMarkerOverlay();
};

const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';
const INITIAL_LISTING_ZOOM = 15;

const sendOpenEvent = () => {
  SoyuzAnalytics.sendGA4Event('interactive-map', { action: 'open-map' });
};

const sendSearchEvent = place => {
  const { name, lat, lng } = place;
  if (name && lat && lng) {
    SoyuzAnalytics.sendGA4Event(PLACE_SELECTED_EVENT_CATEGORY, {
      place: name,
      coord: `${lat},${lng}`
    });
  }
};

const fitToBoth = (map, propertyLatLng, poi) => {
  if (!poi) {
    map.setCenter(propertyLatLng);
    return;
  }

  const bounds = new window.woosmap.map.LatLngBounds();
  bounds.extend(propertyLatLng);
  bounds.extend({ lat: poi.lat, lng: poi.lng });
  map.fitBounds(bounds);
};

const InteractiveMap = ({ mapId, coords, areaSlug, countryCode }) => {
  const [pointOfInterest, setPointOfInterest] = useState(null);
  const map = useRef(null);
  const propertyMarker = useRef(null);
  const poiMarker = useRef(null);
  const poiCircle = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const poiResponse = await getPoiRequest(areaSlug);
        setPointOfInterest(poiResponse.data.poi);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in POI request', error);
      }
    })();
  }, []);

  useEffect(() => {
    const container = document.getElementById(mapId);
    if (!container) return;

    const [lng, lat] = coords;
    const propertyLatLng = { lat, lng };

    map.current = new window.woosmap.map.Map(container, {
      center: propertyLatLng,
      zoom: INITIAL_LISTING_ZOOM
    });

    propertyMarker.current = createPropertyMarkerOverlay(propertyLatLng);
    propertyMarker.current.setMap(map.current);

    sendOpenEvent();

    return () => {
      if (poiMarker.current) {
        poiMarker.current.setMap(null);
        poiMarker.current = null;
      }
      if (poiCircle.current) {
        poiCircle.current.setMap(null);
        poiCircle.current = null;
      }
      if (propertyMarker.current) {
        propertyMarker.current.setMap(null);
        propertyMarker.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current) return;

    if (poiMarker.current) {
      poiMarker.current.setMap(null);
      poiMarker.current = null;
    }
    if (poiCircle.current) {
      poiCircle.current.setMap(null);
      poiCircle.current = null;
    }

    if (pointOfInterest) {
      const position = {
        lat: pointOfInterest.lat,
        lng: pointOfInterest.lng
      };
      poiMarker.current = createPoiMarkerOverlay(position);
      poiMarker.current.setMap(map.current);
      poiCircle.current = new window.woosmap.map.Circle({
        map: map.current,
        center: position,
        radius: POI_CIRCLE_RADIUS_METERS,
        strokeColor: POI_CIRCLE_COLOR,
        strokeOpacity: 1,
        strokeWeight: 1,
        fillColor: POI_CIRCLE_COLOR,
        fillOpacity: 0.25
      });
    }

    const [lng, lat] = coords;
    fitToBoth(map.current, { lat, lng }, pointOfInterest);
  }, [pointOfInterest]);

  const handlePlaceSelected = place => {
    if (place) {
      setPointOfInterest(place);
      setPoiRequest(areaSlug, place);
      sendSearchEvent(place);
    } else {
      setPointOfInterest(null);
      deletePoiRequest(areaSlug);
    }
  };

  const coordsObj = {
    lat: parseFloat(coords[1]),
    lng: parseFloat(coords[0])
  };

  return (
    <Fragment>
      <div className="interactive-map__header">
        <PlacesAutocomplete
          flat
          boundCoords={coordsObj}
          pointOfInterest={pointOfInterest}
          onPlaceSelected={handlePlaceSelected}
          countryCode={countryCode}
        />
      </div>
      <div
        className="interactive-map__map interactive-map__map--woosmap"
        id={mapId}
      />
    </Fragment>
  );
};

InteractiveMap.propTypes = {
  coords: PropTypes.arrayOf(PropTypes.number).isRequired,
  mapId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  areaSlug: PropTypes.string.isRequired
};

export default InteractiveMap;
